import React, { useState, useEffect } from 'react';
import { Layout, Menu, message, Spin, Card, Input, Button, List, Tag, Space, Typography, Table } from 'antd';
import { UploadOutlined, SearchOutlined, FileTextOutlined, HeartOutlined } from '@ant-design/icons';
import DocumentUpload from './components/DocumentUpload';
import DocumentDetail from './components/DocumentDetail';
import { ragApi, Article } from './services/ragApi';

const { Header, Content, Sider } = Layout;
const { Search } = Input;
const { Paragraph, Text } = Typography;

interface SearchResult {
  title?: string;
  content?: string;
  article_title?: string;
  article_content?: string;
  article_id?: string;
  similarity: number;
  document_type?: string;
  author?: string;
  dynasty?: string;
  artist?: string;
}

enum SearchType {
  Normal = 'normal',
  Rerank = 'rerank'
}


const DemoApp: React.FC = () => {
  const [selectedKey, setSelectedKey] = useState('search');
  const [loading, setLoading] = useState(true);
  const [systemHealth, setSystemHealth] = useState<any>(null);
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [documents, setDocuments] = useState<Article[]>([]);
  const [searchType, setSearchType] = useState<SearchType>(SearchType.Normal);
  const [showDocumentDetail, setShowDocumentDetail] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState<SearchResult | null>(null);

    useEffect(() => {
    checkSystemHealth();
  }, []);

  const checkSystemHealth = async () => {
    try {
      const response = await ragApi.healthCheck();
      setSystemHealth(response);
      setLoading(false);
    } catch (error) {
      console.error('Health check failed:', error);
      message.error('系統連接失敗，請檢查後端服務是否正常運行。');
      setLoading(false);
    }
  };

  
  const loadDocuments = async () => {
    setLoading(true);
    try {
      const response = await ragApi.getArticles();
      setDocuments(response.documents || []);
    } catch (error) {
      console.error('Failed to load documents:', error);
      message.error('載入文檔列表失敗');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async (query: string) => {
    if (!query.trim()) {
      message.warning('請輸入搜索關鍵詞');
      return;
    }

    setSearchLoading(true);
    try {
      let response;
      if (searchType === SearchType.Normal) {
        response = await ragApi.searchDocuments({ query, top_k: 10 });
        message.success(`找到 ${response.results?.length || 0} 個相關結果 (普通搜索)`);
      } else {
        response = await ragApi.searchAndRerankDocuments({ query, top_k: 10 });
        message.success(`找到 ${response.results?.length || 0} 個相關結果 (AI重排序)`);
      }
      setSearchResults(response.results || []);
    } catch (error: any) {
      console.error('Search failed:', error);
      message.error(`搜索失敗: ${error.response?.data?.detail || error.message}`);
    } finally {
      setSearchLoading(false);
    };
  };

  const handleDocumentClick = (document: SearchResult) => {
    setSelectedDocument(document);
    setShowDocumentDetail(true);
  };

  const handleBackToSearch = () => {
    setShowDocumentDetail(false);
    setSelectedDocument(null);
  };



  const menuItems = [
    {
      key: 'upload',
      icon: <UploadOutlined />,
      label: '文檔上傳',
    },
    {
      key: 'search',
      icon: <SearchOutlined />,
      label: '文檔搜索',
    },
    {
      key: 'manage',
      icon: <FileTextOutlined />,
      label: '文檔管理',
    },
      ];

  const renderSearchContent = () => (
    <div style={{ padding: '24px' }}>
      <Card title="智能文檔搜索" style={{ marginBottom: '24px' }}>
        <div style={{ marginBottom: '16px' }}>
          <Space.Compact>
            <Button 
              type={searchType === SearchType.Normal ? 'primary' : 'default'}
              onClick={() => setSearchType(SearchType.Normal)}
            >
              普通搜索
            </Button>
            <Button 
              type={searchType === SearchType.Rerank ? 'primary' : 'default'}
              onClick={() => setSearchType(SearchType.Rerank)}
            >
              AI重排序搜索
            </Button>
          </Space.Compact>
          <div style={{ marginTop: '8px' }}>
            <Text type="secondary">
              {searchType === SearchType.Normal ? 
                '普通搜索：基於向量相似度的搜索結果' : 
                'AI重排序搜索：使用大語言模型對搜索結果進行重排序，提高相關性'}
            </Text>
          </div>
        </div>
        <Search
          placeholder="輸入搜索關鍵詞，如：唐代書法、宋代繪畫、古代印章..."
          allowClear
          enterButton="搜索"
          size="large"
          onSearch={handleSearch}
          loading={searchLoading}
        />
      </Card>

      {searchResults.length > 0 && (
        <Card title={`搜索結果 (${searchResults.length})`}>
          <List
            itemLayout="vertical"
            dataSource={searchResults}
            renderItem={(item, index) => (
              <List.Item
                key={index}
                extra={
                  <div>
                    <Tag color="blue">相似度: {(item.similarity * 100).toFixed(1)}%</Tag>
                    {item.document_type && <Tag>{item.document_type}</Tag>}
                    {item.dynasty && <Tag color="gold">{item.dynasty}</Tag>}
                  </div>
                }
              >
                <List.Item.Meta
                  title={
                    <a 
                      href="#" 
                      onClick={(e) => {
                        e.preventDefault();
                        handleDocumentClick(item);
                      }}
                      style={{ color: '#1890ff' }}
                    >
                      {item.article_title || item.title}
                    </a>
                  }
                  description={
                    <div>
                      {item.author && <Text type="secondary">作者: {item.author} | </Text>}
                      {item.artist && <Text type="secondary">藝術家: {item.artist} | </Text>}
                    </div>
                  }
                />
                <Paragraph ellipsis={{ rows: 2, expandable: true }}>
                  {item.article_content || item.content}
                </Paragraph>
              </List.Item>
            )}
          />
        </Card>
      )}
    </div>
  );

  useEffect(() => {
    if (selectedKey === 'manage') {
      loadDocuments();
    }
  }, [selectedKey]);

  const renderManageContent = () => {
    const columns = [
      {
        title: '標題',
        dataIndex: 'title',
        key: 'title',
        render: (text: string) => text || '無標題',
      },
      {
        title: '摘要',
        dataIndex: 'summary',
        key: 'summary',
        width: 300,
        render: (text: string) => (
          <Paragraph ellipsis={{ rows: 2, expandable: true }}>
            {text || '暫無摘要'}
          </Paragraph>
        ),
      },
      {
        title: '發布時間',
        dataIndex: 'published_at',
        key: 'published_at',
        width: 150,
        render: (text: string) => text ? new Date(text).toLocaleDateString() : '未知',
      },
      {
        title: '操作',
        key: 'action',
        render: (_: any, record: any) => (
          <Button 
            type="primary"
            href={record.article_url}
            target="_blank"
            rel="noopener noreferrer"
            disabled={!record.article_url}
          >
            查看原文
          </Button>
        ),
      },
    ];

    return (
      <div style={{ padding: '24px' }}>
        <Card 
          title="文檔管理" 
          extra={
            <Button onClick={loadDocuments} loading={loading}>
              刷新列表
            </Button>
          }
        >
          <Table 
            dataSource={documents}
            columns={columns} 
            rowKey="id"
            loading={loading}
            scroll={{ x: 'max-content' }}
            pagination={false}
          />
        </Card>
      </div>
    );
  };

  
  const renderContent = () => {
    switch (selectedKey) {
            case 'upload':
        return <DocumentUpload onUploadSuccess={(result: any) => {
          message.success(`文檔上傳成功！生成了 ${result.chunks_count} 個文本塊`);
        }} />;
      case 'search':
        return renderSearchContent();
      case 'manage':
        return renderManageContent();
            default:
        return <DocumentUpload onUploadSuccess={(result: any) => {
          message.success(`文檔上傳成功！生成了 ${result.chunks_count} 個文本塊`);
        }} />;
    }
  };

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        flexDirection: 'column'
      }}>
        <div style={{ textAlign: 'center' }}>
          <Spin size="large" />
          <div style={{ marginTop: '16px' }}>
            <Text>正在連接演示系統...</Text>
          </div>
        </div>
        <div style={{ marginTop: '16px', textAlign: 'center' }}>
          <Text type="secondary">請確保後端服務運行在 http://localhost:8000</Text>
        </div>
      </div>
    );
  }

  if (showDocumentDetail && selectedDocument) {
    return (
      <Layout style={{ minHeight: '100vh' }}>
        <Header style={{ 
          display: 'flex', 
          alignItems: 'center', 
          background: '#001529',
          color: 'white',
          padding: '0 24px'
        }}>
          <div style={{ 
            color: 'white', 
            fontSize: '20px', 
            fontWeight: 'bold',
            marginRight: '24px'
          }}>
            古文物藝術品鑑定系統 - 文檔詳情
          </div>
        </Header>
        <Layout>
          <Content style={{ background: '#fff' }}>
            <DocumentDetail
              documentId={selectedDocument.article_id || 'unknown'}
              documentTitle={selectedDocument.article_title || selectedDocument.title || '未知標題'}
              documentContent={selectedDocument.article_content || selectedDocument.content || '無內容'}
              onBack={handleBackToSearch}
            />
          </Content>
        </Layout>
      </Layout>
    );
  }

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Header style={{ 
        display: 'flex', 
        alignItems: 'center', 
        background: '#001529',
        color: 'white',
        padding: '0 24px'
      }}>
        <div style={{ 
          color: 'white', 
          fontSize: '20px', 
          fontWeight: 'bold',
          marginRight: '24px'
        }}>
          古文物藝術品鑑定系統 - 演示版
        </div>
        {systemHealth && (
          <div style={{ 
            marginLeft: 'auto',
            display: 'flex',
            alignItems: 'center',
            color: '#52c41a'
          }}>
            <HeartOutlined style={{ marginRight: '8px' }} />
            演示模式運行中
            {systemHealth.demo_mode && (
              <Tag color="orange" style={{ marginLeft: '8px' }}>DEMO</Tag>
            )}
          </div>
        )}
      </Header>
      
      <Layout>
        <Sider 
          width={200} 
          style={{ background: '#fff' }} 
          breakpoint="lg" // 在 lg 斷點（992px）以下收起
          collapsedWidth="0" // 收起時寬度為0
        >
          <Menu
            mode="inline"
            selectedKeys={[selectedKey]}
            style={{ height: '100%', borderRight: 0 }}
            items={menuItems}
            onClick={({ key }) => setSelectedKey(key)}
          />
        </Sider>
        
        <Layout style={{ padding: '24px' }}>
          <Content
            style={{
              background: '#fff',
              padding: 24,
              margin: 0,
              minHeight: 280,
            }}
          >
            {renderContent()}
          </Content>
        </Layout>
      </Layout>
    </Layout>
  );
};

export default DemoApp;